import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import {
  QuestionGroup,
  QuestionGroupsMap,
  PaginatedQuestionGroupResponse,
} from '../types/QuestionGroup';
import { toScoreLabel } from '../components/utils/scoreConverter/toScoreLabel';
import { toTotalScoreLabel } from '../components/utils/scoreConverter/toTotalScoreLabel';
import { Question } from '../types/Question';

// 難易度ごとのページネーション状態
type PaginationState = {
  cursor: string | null;
  hasMore: boolean;
};

type PaginationMap = {
  beginner: PaginationState;
  easy: PaginationState;
  normal: PaginationState;
  hard: PaginationState;
};

const initialPaginationState: PaginationState = {
  cursor: null,
  hasMore: true,
};

// 難易度ごとの問題集リスト、ローディング判定値、エラー判定値を返す
const useQuestionGroups = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [questionGroupsMap, setQuestionGroupsMap] =
    useState<QuestionGroupsMap>();
  const [paginationMap, setPaginationMap] = useState<PaginationMap>({
    beginner: { ...initialPaginationState },
    easy: { ...initialPaginationState },
    normal: { ...initialPaginationState },
    hard: { ...initialPaginationState },
  });

  useEffect(() => {
    const fetchQuestionGroups = async () => {
      setLoading(true);
      try {
        const [beginnerResult, easyResult, normalResult, hardResult] =
          await Promise.all([
            getQuestionGroups('beginner', null, 0),
            getQuestionGroups('easy', null, 0),
            getQuestionGroups('normal', null, 0),
            getQuestionGroups('hard', null, 0),
          ]);

        const newQuestionGroupsMap = {
          beginner: beginnerResult.items,
          easy: easyResult.items,
          normal: normalResult.items,
          hard: hardResult.items,
        };
        setQuestionGroupsMap(newQuestionGroupsMap);

        setPaginationMap({
          beginner: {
            cursor: beginnerResult.nextCursor,
            hasMore: beginnerResult.hasMore,
          },
          easy: {
            cursor: easyResult.nextCursor,
            hasMore: easyResult.hasMore,
          },
          normal: {
            cursor: normalResult.nextCursor,
            hasMore: normalResult.hasMore,
          },
          hard: {
            cursor: hardResult.nextCursor,
            hasMore: hardResult.hasMore,
          },
        });
      } catch (error) {
        console.error('Error fetching QuestionGroups:', error);
        setError(true);
      } finally {
        setLoading(false);
        console.log('loading set to false');
      }
    };
    fetchQuestionGroups();
  }, []);

  const getQuestionGroups = async (
    difficulty: string,
    cursor: string | null,
    startIndex: number,
  ): Promise<{
    items: QuestionGroup[];
    nextCursor: string | null;
    hasMore: boolean;
  }> => {
    const { data } = await axios<PaginatedQuestionGroupResponse>(
      `${process.env.REACT_APP_PUBLIC_API_URL}/get_question_group`,
      {
        params: {
          difficulty,
          limit: 20,
          ...(cursor && { cursor }),
        },
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
        },
        withCredentials: true,
      },
    );

    // バックエンドで未来日付のフィルタリングを実施済み
    const questionGroups: QuestionGroup[] = data.items.map(
      (questionGroup, index: number) => {
        const displayNumber = parseInt(questionGroup.displayQuestionNumber);
        const shuffledQuestions = shuffleOrder(questionGroup.questions);

        const questions =
          displayNumber > 0
            ? shuffledQuestions.slice(0, displayNumber)
            : shuffledQuestions;

        const bestScoreLabel = {
          time: '-',
          accuracy: '-',
          total: '-',
        };

        if (questionGroup.bestScore) {
          bestScoreLabel.time = toScoreLabel(questionGroup.bestScore.time);
          bestScoreLabel.accuracy = toScoreLabel(
            questionGroup.bestScore.accuracy,
          );
          bestScoreLabel.total = toTotalScoreLabel(
            questionGroup.bestScore.total,
          );
        }

        return {
          ...questionGroup,
          key: startIndex + index,
          questions,
          bestScoreLabel,
        };
      },
    );

    return {
      items: questionGroups,
      nextCursor: data.pagination.nextCursor,
      hasMore: data.pagination.hasMore,
    };
  };

  // 追加読み込み関数
  const loadMore = useCallback(
    async (difficulty: 'beginner' | 'easy' | 'normal' | 'hard') => {
      const pagination = paginationMap[difficulty];
      if (!pagination.hasMore || loadingMore) return;

      const currentItems = questionGroupsMap?.[difficulty] ?? [];

      setLoadingMore(true);
      try {
        const result = await getQuestionGroups(
          difficulty,
          pagination.cursor,
          currentItems.length,
        );

        setQuestionGroupsMap((prev) => ({
          beginner: prev?.beginner ?? [],
          easy: prev?.easy ?? [],
          normal: prev?.normal ?? [],
          hard: prev?.hard ?? [],
          [difficulty]: [...currentItems, ...result.items],
        }));

        setPaginationMap((prev) => ({
          ...prev,
          [difficulty]: {
            cursor: result.nextCursor,
            hasMore: result.hasMore,
          },
        }));
      } catch (error) {
        console.error('Error loading more QuestionGroups:', error);
      } finally {
        setLoadingMore(false);
      }
    },
    [paginationMap, questionGroupsMap, loadingMore],
  );

  return {
    questionGroupsMap,
    error,
    loading,
    loadingMore,
    paginationMap,
    loadMore,
  };
};

const shuffleOrder = (questions: Question[]): Question[] => {
  const array = [...questions];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

export default useQuestionGroups;
